import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useToast } from "../App";
import PipelineNav from "../components/PipelineNav";
import ProgressSteps from "../components/ProgressSteps";
import Uploader from "../components/Uploader";
import { api } from "../lib/api";
import { bytes } from "../lib/format";
import { useJobStream } from "../lib/useJobStream";
import type { ProjectDetail, Source } from "../lib/types";

const KIND_LABEL: Record<string, string> = {
  pdf: "PDF",
  docx: "DOC",
  markdown: "MD",
  text: "TXT",
  image: "IMG",
};

export default function SourcesPage() {
  const { projectId = "" } = useParams();
  const { pushError, push } = useToast();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [reprocessing, setReprocessing] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setProject(await api.getProject(projectId));
    } catch (error) {
      pushError(error);
    }
  }, [projectId, pushError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Reattach to an ingest job that was already running when the page loaded.
  useEffect(() => {
    api
      .listJobs(projectId)
      .then((jobs) => {
        const live = jobs.find(
          (j) => (j.status === "running" || j.status === "queued") && j.kind !== "generate",
        );
        if (live) setJobId(live.id ?? live.job_id ?? null);
      })
      .catch(() => {});
  }, [projectId]);

  const onJobDone = useCallback(() => refresh(), [refresh]);
  const job = useJobStream(jobId, onJobDone);
  const running = job?.status === "running" || job?.status === "queued";

  const upload = async (files: File[]) => {
    setUploading(true);
    try {
      const result = await api.uploadSources(projectId, files);
      result.skipped.forEach((s) => push(`${s.filename}: ${s.reason}`, "error"));
      setJobId(result.job_id);
      await refresh();
    } catch (error) {
      pushError(error);
    } finally {
      setUploading(false);
    }
  };

  const removeSource = async (source: Source) => {
    try {
      await api.deleteSource(projectId, source.id);
      await refresh();
    } catch (error) {
      pushError(error);
    }
  };

  const reprocessSource = async (source: Source) => {
    setReprocessing(source.id);
    try {
      const { job_id } = await api.reprocessSource(projectId, source.id);
      setJobId(job_id);
    } catch (error) {
      pushError(error);
    } finally {
      setReprocessing(null);
    }
  };

  if (!project) return <main className="page"><p className="muted">Loading…</p></main>;

  return (
    <main className="page">
      <PipelineNav projectId={projectId} projectName={project.name} current="sources" />

      <div className="page-head">
        <div className="grow">
          <h1>Sources</h1>
          <p>Upload your study material. Text is extracted before anything is generated.</p>
        </div>
        <Link to={`/projects/${projectId}/generate`} className="btn btn-primary btn-lg">
          Generate cards →
        </Link>
      </div>

      <div className="stack">
        <div className="panel">
          <Uploader onFiles={upload} disabled={uploading || running} />
        </div>

        {job && (
          <div className="panel">
            <ProgressSteps job={job} />
          </div>
        )}

        {project.sources.length > 0 && (
          <div className="panel">
            <h2 style={{ marginBottom: 12 }}>{project.sources.length} source{project.sources.length === 1 ? "" : "s"}</h2>
            <div className="stack" style={{ gap: 6 }}>
              {project.sources.map((source) => (
                <div key={source.id} className="file-row">
                  <span className="file-icon">{KIND_LABEL[source.kind] ?? "?"}</span>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {source.filename}
                    </div>
                    <div className="small faint">
                      {bytes(source.size_bytes)}
                      {statSummary(source)}
                    </div>
                    {source.error && (
                      <div className="small" style={{ color: "var(--bad)" }}>
                        {source.error}
                      </div>
                    )}
                  </div>
                  <StatusChip status={source.status} />
                  {source.status === "failed" && (
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => reprocessSource(source)}
                      disabled={running || reprocessing === source.id}
                      title="Reprocess"
                    >
                      {reprocessing === source.id ? "…" : "↻"}
                    </button>
                  )}
                  <button
                    className="btn-ghost btn-sm"
                    onClick={() => removeSource(source)}
                    disabled={running}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function StatusChip({ status }: { status: Source["status"] }) {
  const map: Record<Source["status"], { cls: string; text: string }> = {
    pending: { cls: "chip", text: "Queued" },
    processing: { cls: "chip chip-accent", text: "Processing" },
    ready: { cls: "chip chip-good", text: "Ready" },
    failed: { cls: "chip chip-bad", text: "Failed" },
  };
  const entry = map[status];
  return <span className={entry.cls}>{entry.text}</span>;
}

function statSummary(source: Source): string {
  const stats = source.stats ?? {};
  const parts: string[] = [];
  if (typeof stats.pages === "number") parts.push(`${stats.pages} pages`);
  if (typeof stats.duration_seconds === "number")
    parts.push(`${Math.round(stats.duration_seconds / 60)} min`);
  if (typeof stats.chunks === "number") parts.push(`${stats.chunks} passages`);
  if (typeof stats.images === "number" && stats.images > 0) parts.push(`${stats.images} images`);
  return parts.length ? ` · ${parts.join(" · ")}` : "";
}
